import React, { useState, useEffect } from 'react';
import {
  Grid,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  Paper,
  Box,
  Button,
  InputAdornment,
  IconButton,
  Tooltip,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Stack,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Checkbox,
  Collapse
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import {
  Image as ImageIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  Notes as NotesIcon,
  Autorenew as AutorenewIcon,
  Edit as EditIcon,
  AttachMoney as AttachMoneyIcon
} from '@mui/icons-material';
import EnhancedSearch from './common/EnhancedSearch';
import DurationSelector from './DurationSelector';
import PricingService from '../services/pricingService';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { API_URL } from '../config';
import { normalizeImageDataUrl } from '../utils/imageDataUtils';
import { compressImageIfNeeded, getCompressionOptions } from '../utils/imageCompression';
import OrderCreationAttachments from './OrderCreationAttachments';
import OrderAttachments from './OrderAttachments';
import NoteCard from './notes/NoteCard';
import { createOfferCreationNote, parseNotesBlob } from '../utils/noteUtils';


// Helper function to calculate weeks between two dates
const calculateWeeks = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  if (isNaN(start.getTime()) || isNaN(end.getTime()) || end <= start) {
    return 0;
  }
  
  const diffTime = Math.abs(end - start);
  const diffWeeks = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 7));
  return diffWeeks;
};

// Helper function to check if school has Top Spot with insufficient duration
const hasTopSpotDurationIssue = (school, group) => {
  if (!school.addons?.topSpot) return false;
  const weeks = calculateWeeks(group.startDate, group.endDate);
  return weeks > 0 && weeks < 12;
};

const OfferForm = ({ 
  formData, 
  setFormData, 
  organizations, 
  handleAudienceChange, 
  addAudience, 
  removeAudience,
  isEditingOffer = false,
  editingOfferId = null,
  onTopSpotConflictsChange = null,
  lockedOrganization = null
}) => {
  const { getToken, user } = useAuth();
  const [priceCalculationTimeouts, setPriceCalculationTimeouts] = useState({});
  // State for the "Change Price" per-school menu
  const [priceMenuAnchor, setPriceMenuAnchor] = useState(null);
  const [priceMenuTarget, setPriceMenuTarget] = useState(null); // { groupId, schoolId }
  const [changeRequests, setChangeRequests] = useState([]);
  const [loadingChangeRequests, setLoadingChangeRequests] = useState(false);
  const [imageViewerDialog, setImageViewerDialog] = useState({ open: false, imageUrl: '', imageName: '' });
  const [topSpotConflicts, setTopSpotConflicts] = useState({});
  
  // Store the selected business org object directly so the EnhancedSearch value prop
  // doesn't need to scan the limited local `organizations` array (large DB issue).
  const [selectedBusinessOrg, setSelectedBusinessOrg] = useState(null);

  // State for attachments during offer creation
  const [tempAttachments, setTempAttachments] = useState([]);
  
  // State for saved attachments (when editing existing offer)
  const [savedAttachments, setSavedAttachments] = useState([]);
  
  // State for notes
  const [offerNotes, setOfferNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [noteType, setNoteType] = useState('general');
  
  // State for groups (replacing audiences)
  // Each group has: channel, adType, startDate, endDate, groupDiscount, groupDiscountType, addons (gif), and schools array
  // Each school has: organization_id, price, discount, discountType, addons (including topSpot), manualPriceOverride
  const [groups, setGroups] = useState([
    {
      id: 1,
    channel: '',
    adType: '',
    startDate: '',
    endDate: '',
      groupDiscount: '',
      groupDiscountType: '$',
      addons: {},
      schools: [
        {
          id: 1,
          organization_id: '',
          price: '',
    discount: '',
          discountType: '$',
          addons: {},
          manualPriceOverride: false
        }
      ]
    }
  ]);

  // Fetch saved attachments when editing an offer
  useEffect(() => {
    const fetchOfferAttachments = async () => {
      if (isEditingOffer && editingOfferId) {
        try {
          const token = await getToken();
          const response = await fetch(`${API_URL}/offer-attachments/${editingOfferId}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.success && data.data) {
              setSavedAttachments(data.data);
            }
          }
        } catch (error) {
          console.error('Error fetching offer attachments:', error);
        }
      } else {
        // Clear saved attachments when creating a new offer
        setSavedAttachments([]);
      }
    };
    
    fetchOfferAttachments();
  }, [isEditingOffer, editingOfferId, getToken]);

  // Initialize notes from formData when editing (only once on mount).
  // Each blob may contain several notes joined together (added across
  // different stages/sessions) - parse them into individually attributed
  // notes instead of one note per field.
  useEffect(() => {
    if (isEditingOffer && editingOfferId && (formData.salesNotes || formData.designNotes || formData.customerNotes)) {
      const notes = [
        ...parseNotesBlob(formData.salesNotes, { idPrefix: 'sales', noteType: 'general' }),
        ...parseNotesBlob(formData.designNotes, { idPrefix: 'design', noteType: 'design' }),
        ...parseNotesBlob(formData.customerNotes, { idPrefix: 'customer', noteType: 'customer' })
      ];

      setOfferNotes(notes);
    }
    // Only run once when editing offer ID changes, not when formData notes change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditingOffer, editingOfferId]);

  // Sync groups to formData.audiences for backend compatibility
  useEffect(() => {
    const audiences = [];
    groups.forEach((group) => {
      group.schools.forEach((school) => {
        // Apply group discount if school has no individual discount (handles empty string, 0, or NaN)
        const hasSchoolDiscount = parseFloat(school.discount) > 0;
        const effectiveDiscount = (!hasSchoolDiscount && parseFloat(group.groupDiscount) > 0) 
          ? group.groupDiscount 
          : school.discount;
        const effectiveDiscountType = (!hasSchoolDiscount && parseFloat(group.groupDiscount) > 0)
          ? group.groupDiscountType
          : school.discountType;
        
        // Merge group addons (gif) with school addons (topSpot)
        const mergedAddons = {
          ...(group.addons || {}),
          ...(school.addons || {})
        };
        
        audiences.push({
          organization_id: school.organization_id,
          channel: group.channel,
          adType: group.adType,
          adGroup: group.id,
          startDate: group.startDate,
          endDate: group.endDate,
          addons: mergedAddons,
          price: school.price,
          discount: effectiveDiscount,
          discountType: effectiveDiscountType,
          manualPriceOverride: school.manualPriceOverride || false
        });
      });
    });
    setFormData(prev => ({ ...prev, audiences }));
  }, [groups]);

  // Initialize groups from formData.audiences when editing
  useEffect(() => {
    if (isEditingOffer && formData.audiences && formData.audiences.length > 0) {
      // Group audiences by adGroup (or fall back to channel-adType-startDate-endDate)
      const groupMap = new Map();
      
      formData.audiences.forEach((audience) => {
        // Use adGroup as the primary grouping key so multi-school groups are rebuilt correctly
        const key = audience.adGroup != null
          ? String(audience.adGroup)
          : `${audience.channel}-${audience.adType}-${audience.startDate}-${audience.endDate}`;
        
        if (!groupMap.has(key)) {
          // Extract group-level addons (gif)
          const groupAddons = {};
          if (audience.addons?.gif) {
            groupAddons.gif = audience.addons.gif;
          }
          
          groupMap.set(key, {
            id: groupMap.size + 1,
            channel: audience.channel,
            adType: audience.adType,
            startDate: audience.startDate,
            endDate: audience.endDate,
            groupDiscount: '0',
            groupDiscountType: '$',
            addons: groupAddons,
            schools: []
          });
        }
        
        // Extract school-level addons (topSpot) - preserve boolean value exactly
        const schoolAddons = {};
        if (audience.addons?.topSpot) {
          schoolAddons.topSpot = true;
        }
        
        groupMap.get(key).schools.push({
          id: groupMap.get(key).schools.length + 1,
          organization_id: audience.organization_id,
          price: audience.price != null ? String(audience.price) : '',
          discount: audience.discount != null ? String(audience.discount) : '0',
          discountType: audience.discountType || '$',
          addons: schoolAddons,
          // When editing an existing offer the price is always locked by default so
          // a stale org-level price change never silently overwrites the saved value.
          // The user can explicitly unlock it via the "Change Price" menu.
          manualPriceOverride: isEditingOffer ? true : (audience.manualPriceOverride || false)
        });
      });
      
      const initialGroups = Array.from(groupMap.values());
      if (initialGroups.length > 0) {
        setGroups(initialGroups);
      }
    }
  // Re-run when the editing offer ID changes (e.g. switching between offers)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditingOffer, editingOfferId]);

  // Populate _selectedOrg for any school rebuilt from saved data (editing / creating
  // from offer). Without this the EnhancedSearch value prop is null so the school
  // name doesn't appear in the dropdown even though the price and data are correct.
  useEffect(() => {
    const missing = [];
    groups.forEach(group => {
      group.schools.forEach(school => {
        if (school.organization_id && !school._selectedOrg) {
          missing.push(school.organization_id);
        }
      });
    });
    if (missing.length === 0) return;

    let cancelled = false;
    const fetchMissingOrgs = async () => {
      const token = await getToken();
      const unique = [...new Set(missing)];
      const fetched = {};
      await Promise.all(unique.map(async (orgId) => {
        try {
          const resp = await fetch(`${API_URL}/organizations/${orgId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (resp.ok) {
            const data = await resp.json();
            if (data && data.id) fetched[orgId] = data;
          }
        } catch (e) {
          console.error('Failed to fetch org for school display:', orgId, e);
        }
      }));
      if (cancelled || Object.keys(fetched).length === 0) return;
      setGroups(prev => prev.map(group => ({
        ...group,
        schools: group.schools.map(school => {
          if (school.organization_id && !school._selectedOrg && fetched[school.organization_id]) {
            return { ...school, _selectedOrg: fetched[school.organization_id] };
          }
          return school;
        })
      })));
    };
    fetchMissingOrgs();
    return () => { cancelled = true; };
  // groups is the only dep we need — getToken and API_URL are stable references
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);

  // When editing an existing offer, seed selectedBusinessOrg from formData so the
  // EnhancedSearch "Select Business" dropdown shows the already-chosen business.
  // We only need { id, name } for display — no extra fetch required.
  useEffect(() => {
    if (isEditingOffer && formData.organization_id && !selectedBusinessOrg) {
      setSelectedBusinessOrg({
        id: formData.organization_id,
        name: formData.business_name || ''
      });
    }
  // Run whenever the editing offer ID changes (switching between offers)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditingOffer, editingOfferId]);

  // Handle attachment changes
  const handleAttachmentsChange = (attachments) => {
    setTempAttachments(attachments);
    // Update form data with temp attachments
    setFormData(prev => ({
      ...prev,
      tempAttachments: attachments
    }));
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(priceCalculationTimeouts).forEach(timeoutId => {
        if (timeoutId) clearTimeout(timeoutId);
      });
    };
  }, [priceCalculationTimeouts]);

  // Fetch change requests when editing an offer
  useEffect(() => {
    const fetchChangeRequests = async () => {
      if (isEditingOffer && editingOfferId) {
        setLoadingChangeRequests(true);
        try {
          const token = await getToken();
          const response = await axios.get(`${API_URL}/offers/${editingOfferId}/change-requests`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (response.data && response.data.success) {
            setChangeRequests(response.data.data);
          }
        } catch (error) {
          console.error('Error fetching change requests:', error);
        } finally {
          setLoadingChangeRequests(false);
        }
      }
    };

    fetchChangeRequests();
  }, [isEditingOffer, editingOfferId, getToken]);

  // Utility functions for handling attachments
  const isImageFile = (filename) => {
    if (!filename) return false;
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
    return imageExtensions.some(ext => filename.toLowerCase().endsWith(ext));
  };

  const getAttachmentUrl = (attachmentPath) => {
    if (!attachmentPath) return '';
    // Remove '/api' from API_URL for static file serving
    const baseUrl = API_URL.replace('/api', '');
    return `${baseUrl}/uploads/${attachmentPath}`;
  };

  const handleViewImage = (attachmentPath, originalName) => {
    setImageViewerDialog({
      open: true,
      imageUrl: getAttachmentUrl(attachmentPath),
      imageName: originalName
    });
  };

  // Handle adding a new note
  const handleAddNote = () => {
    if (!newNote.trim()) return;

    const userName = user?.name || 'Current User';
    // The structured "(date) User from Source: content" string is what actually
    // gets persisted to the backend text column; the note card itself displays
    // the clean content + a short source label instead of this raw string.
    const structuredContent = createOfferCreationNote(newNote.trim(), userName, noteType);

    const noteData = {
      id: Date.now(),
      content: newNote.trim(),
      createdBy: userName,
      createdAt: new Date().toISOString(),
      sourceLabel: 'Created from Offer Creation',
      isNew: true,
      noteType: noteType,
      raw: structuredContent
    };

    // Add to local state
    const updatedNotes = [noteData, ...offerNotes];
    setOfferNotes(updatedNotes);
    setNewNote('');
    setNoteType('general');

    // Update formData with categorized notes, rebuilding the structured string
    // for each note (using its original raw text when available) so nothing
    // loses its "(date) User from Source:" attribution once persisted.
    const salesNotes = [];
    const designNotes = [];
    const customerNotes = [];

    updatedNotes.forEach(note => {
      const stored = note.raw || note.content;
      if (note.noteType === 'design') {
        designNotes.push(stored);
      } else if (note.noteType === 'customer') {
        customerNotes.push(stored);
      } else {
        salesNotes.push(stored);
      }
    });
    
    setFormData(prev => ({
      ...prev,
      salesNotes: salesNotes.join('\n\n'),
      designNotes: designNotes.join('\n\n'),
      customerNotes: customerNotes.join('\n\n')
    }));
  };
  
  // Get total note count
  const getTotalNoteCount = () => {
    return offerNotes.length;
  };

  const handleDownloadAttachment = async (attachmentPath, originalName) => {
    try {
      const url = getAttachmentUrl(attachmentPath);
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = originalName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Download failed:', error);
      // Fallback: open in new tab
      window.open(getAttachmentUrl(attachmentPath), '_blank');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-AU', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Function to get available channels for an organization
  const getAvailableChannels = (organizationId) => {
    if (!organizationId || !Array.isArray(organizations)) return [];
    
    const selectedOrg = organizations.find(org => org.id === parseInt(organizationId));
    if (!selectedOrg) return [];
    
    const channels = [];
    
    // Add channels based on organization properties
    if (selectedOrg.hasSchoolLink === true || selectedOrg.hasSchoolLink === 1) {
      channels.push("SchoolLink");
    }
    
    if (selectedOrg.hasEPublisher === true || selectedOrg.hasEPublisher === 1) {
      channels.push("ePublisher");
    }
    
    if (selectedOrg.hasPaper === true || selectedOrg.hasPaper === 1) {
      channels.push("Paper");
    }
    
    // If this is a school and no channels are available, provide default channels
    if (channels.length === 0 && (selectedOrg.isSchool === true || selectedOrg.isSchool === 1)) {
      // Default channels for schools - can be customized based on business requirements
      channels.push("SchoolLink", "ePublisher", "Paper");
    }
    
    return channels;
  };

  // Function to get available ad types based on selected channel
  const getAvailableAdTypes = (channel) => {
    if (!channel) return [];
    
    switch (channel) {
      case 'Paper':
        return ['Standard', 'Budget', 'Premium'];
      case 'ePublisher':
        return ['Standard', 'Budget', 'Premium', 'POW'];
      case 'SchoolLink':
        return ['Single Image', 'Carousel', 'Video or Animation', 'Standard', 'Premium'];
      default:
        return [];
    }
  };

  // Handle business type toggle change
  const handleBusinessTypeChange = (event) => {
    const businessType = event.target.value;
    setFormData({
      ...formData,
      businessType: businessType,
      organization_id: '', // Reset organization selection
      business_name: '' // Reset business name
    });
  };

  // Add a new group
  const addGroup = () => {
    const newGroupId = Math.max(...groups.map(g => g.id), 0) + 1;
    setGroups([
      ...groups,
      {
        id: newGroupId,
        channel: '',
        adType: '',
        startDate: '',
        endDate: '',
        groupDiscount: '0',
        groupDiscountType: '$',
        addons: {},
        schools: [
          {
            id: 1,
            organization_id: '',
            price: '',
            discount: '0',
            discountType: '$',
            addons: {},
            manualPriceOverride: false
          }
        ]
      }
    ]);
  };

  // Remove a group
  const removeGroup = (groupId) => {
    setGroups(groups.filter(g => g.id !== groupId));
  };

  // Add a school to a group
  const addSchoolToGroup = (groupId) => {
    setGroups(groups.map(group => {
      if (group.id === groupId) {
        const newSchoolId = Math.max(...group.schools.map(s => s.id), 0) + 1;
        return {
          ...group,
          schools: [
            ...group.schools,
            {
              id: newSchoolId,
              organization_id: '',
              price: '',
              discount: '0',
              discountType: '$',
              addons: {},
              manualPriceOverride: false
            }
          ]
        };
      }
      return group;
    }));
  };

  // Remove a school from a group
  const removeSchoolFromGroup = (groupId, schoolId) => {
    setGroups(groups.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          schools: group.schools.filter(s => s.id !== schoolId)
        };
      }
      return group;
    }));
  };

  // Handle group field changes
  const handleGroupChange = (groupId, field, value) => {
    setGroups(groups.map(group => {
      if (group.id === groupId) {
        const updatedGroup = { ...group, [field]: value };
        
        // If channel changes, reset adType and clear prices
        if (field === 'channel') {
          updatedGroup.adType = '';
          // Clear prices for all schools in this group
          updatedGroup.schools = updatedGroup.schools.map(school => ({
            ...school,
            price: ''
          }));
        }
        
        // If adType changes to POW, set end date to 5 weeks after start date
        if (field === 'adType' && value === 'POW' && updatedGroup.startDate) {
          const startDate = new Date(updatedGroup.startDate);
          const endDate = new Date(startDate);
          endDate.setDate(endDate.getDate() + (5 * 7)); // 5 weeks = 35 days
          updatedGroup.endDate = endDate.toISOString().split('T')[0];
        }
        
        // If adType changes from POW to something else, unlock the end date (keep current value)
        // No action needed here as the field will automatically become editable
        
        // If adType or channel changes, clear addons that are no longer supported
        if (field === 'adType' || field === 'channel') {
          // Check if GIF addon should be cleared
          // GIF is only supported for: ePublisher Premium, or POW (ePublisher/Paper)
          const gifSupported = (updatedGroup.channel === 'ePublisher' && updatedGroup.adType === 'Premium') ||
                               (updatedGroup.adType === 'POW' && ['ePublisher', 'Paper'].includes(updatedGroup.channel));
          
          if (!gifSupported && updatedGroup.addons?.gif) {
            updatedGroup.addons = { ...updatedGroup.addons, gif: false };
          }
          
          // Check if Top Spot addon should be cleared for all schools
          // Top Spot is only supported for: ePublisher Standard/Premium (not POW)
          const topSpotSupported = updatedGroup.channel === 'ePublisher' && 
                                   ['Standard', 'Premium'].includes(updatedGroup.adType) &&
                                   updatedGroup.adType !== 'POW';
          
          if (!topSpotSupported) {
            updatedGroup.schools = updatedGroup.schools.map(school => {
              if (school.addons?.topSpot) {
                // Clear top spot conflict for this school
                const conflictKey = `${groupId}-${school.id}`;
                setTopSpotConflicts(prev => {
                  const newConflicts = { ...prev };
                  delete newConflicts[conflictKey];
                  if (onTopSpotConflictsChange) {
                    onTopSpotConflictsChange(newConflicts);
                  }
                  return newConflicts;
                });
                
                return {
                  ...school,
                  addons: { ...school.addons, topSpot: false }
                };
              }
              return school;
            });
          }
        }
        
        // Recalculate prices for all schools in this group when dates, channel, or adType change
        if ((field === 'startDate' || field === 'endDate' || field === 'channel' || field === 'adType') && updatedGroup.channel && updatedGroup.adType && updatedGroup.startDate && updatedGroup.endDate) {
          updatedGroup.schools.forEach((school, schoolIndex) => {
            if (school.organization_id && !school.manualPriceOverride) {
              calculateSchoolPrice(groupId, school.id, school.organization_id, updatedGroup.channel, updatedGroup.adType, updatedGroup.startDate, updatedGroup.endDate, school.addons || {});
            }
          });
        }
        
        return updatedGroup;
      }
      return group;
    }));
  };

  // Handle school field changes
  // orgObject is optionally passed when field === 'organization_id' so we can
  // store the full org object alongside the id. This prevents the EnhancedSearch
  // value prop from needing to look the org up in the limited local `organizations`
  // array (which may only hold a fraction of the orgs when the DB is large).
  const handleSchoolChange = async (groupId, schoolId, field, value, orgObject = null) => {
    const group = groups.find(g => g.id === groupId);
    if (!group) return;
    
    const school = group.schools.find(s => s.id === schoolId);
    if (!school) return;
    
    // Update the state first
    setGroups(groups.map(g => {
      if (g.id === groupId) {
        return {
          ...g,
          schools: g.schools.map(school => {
            if (school.id === schoolId) {
              const updates = { [field]: value };
              // When the org is being set, also cache the full object so the
              // EnhancedSearch value prop resolves without scanning the local array.
              if (field === 'organization_id') {
                updates._selectedOrg = orgObject || null;
              }
              return { ...school, ...updates };
            }
            return school;
          })
        };
      }
      return g;
    }));
    
    // Then handle price calculations based on the field that changed
    // If organization changes, calculate price (only if manual override is not enabled)
    if (field === 'organization_id' && value && group.channel && group.adType && group.startDate && group.endDate && !school.manualPriceOverride) {
      // Pass orgObject directly so calculateSchoolPrice has the full org with all
      // pricing fields (ePublisherCost etc.) without depending on the groups state,
      // which is stale inside the closure at this point (setGroups above is async).
      calculateSchoolPrice(groupId, schoolId, value, group.channel, group.adType, group.startDate, group.endDate, school.addons || {}, false, orgObject);
    }
    
    // If manual price override is being disabled, recalculate price
    if (field === 'manualPriceOverride' && value === false && school.organization_id && group.channel && group.adType && group.startDate && group.endDate) {
      // Force calculation even though the state hasn't updated yet
      setTimeout(() => {
        calculateSchoolPrice(groupId, schoolId, school.organization_id, group.channel, group.adType, group.startDate, group.endDate, school.addons || {}, true);
      }, 0);
    }
  };

  // Function to get organization price, optionally channel-aware.
  // Accepts an optional pre-resolved org object (e.g. _selectedOrg from school state)
  // so we don't have to rely on the limited organizations prop which may be missing
  // pricing fields (ePublisherCost, deployments, etc.) or the school entirely.
  const getOrganizationPrice = (organizationId, channel = null, resolvedOrg = null) => {
    let org = resolvedOrg;
    if (!org) {
      if (!Array.isArray(organizations)) return null;
      org = organizations.find(o => o.id === organizationId || o.id === parseInt(organizationId));
    }
    if (!org) return null;

    // For ePublisher channel on schools: prefer ePublisherCost as the annual base price
    if (channel === 'ePublisher' && (org.isSchool === true || org.isSchool === 1)) {
      if (org.ePublisherCost && parseFloat(org.ePublisherCost) > 0) {
        return parseFloat(org.ePublisherCost);
      }
    }
    
    // If organization has a price set, use it
    if (org.price && org.price > 0) {
      return parseFloat(org.price);
    }
    
    // For schools without a price, calculate it based on reach
    if (org.isSchool === true || org.isSchool === 1) {
      const deployments = parseInt(org.deployments || org.issuesPerTerm, 10) || 0;
      // Effective subscriber count: ePublisherSubscribers, or numberOfFamilies * 2 when 0
      const explicitSubs = parseInt(org.ePublisherSubscribers, 10);
      const effectiveSubs = (!explicitSubs && org.numberOfFamilies)
        ? parseInt(org.numberOfFamilies, 10) * 2
        : explicitSubs || parseInt(org.subscribers || org.numberOfFamilies, 10) || 0;
      
      if (deployments > 0 && effectiveSubs > 0) {
        const reach = (deployments * 4) * effectiveSubs;
        
        // Calculate price based on reach: $900 + (Reach - 5000) x 0.025
        const calculatedPrice = reach > 5000 
          ? 900 + (reach - 5000) * 0.025 
          : 900;
        
        return calculatedPrice;
      }
      
      // Fallback for schools without deployment/subscriber data
      return 900; // Minimum school price
    }
    
    return null;
  };

  // Calculate price for a specific school.
  // resolvedOrg: the full org object (with all DB fields) if already available at the
  // call site. This avoids a stale-closure read of the groups state, which is the case
  // when called immediately after a setGroups() call (React batches state updates so
  // the groups variable in the closure hasn't changed yet).
  const calculateSchoolPrice = async (groupId, schoolId, organizationId, channel, adType, startDate, endDate, addons = {}, forceCalculate = false, resolvedOrg = null) => {
    try {
      // Resolve the school's full org object, in priority order:
      //   1. resolvedOrg passed by the caller (fresh, avoids stale closure)
      //   2. _selectedOrg cached in the groups state from a previous render
      //      (reliable for date/channel/adType change paths where the school was
      //       already selected in an earlier render)
      //   3. Fallback: search the limited organizations prop (may be missing the
      //      school entirely, and lacks pricing fields like ePublisherCost)
      const groupEntry = groups.find(g => g.id === groupId);
      const schoolEntry = groupEntry?.schools.find(s => s.id === schoolId);
      const selectedSchool = resolvedOrg ||
        schoolEntry?._selectedOrg ||
        (Array.isArray(organizations)
          ? organizations.find(org => org.id === organizationId || org.id === parseInt(organizationId))
          : null);
      
      if (!selectedSchool || !channel || !adType || !startDate || !endDate) {
        return;
      }

      // Check if manual price override is enabled for this school (unless forced)
      if (!forceCalculate) {
        if (schoolEntry?.manualPriceOverride) {
          // Don't calculate price if manual override is enabled
          return;
        }
      }

      // Get the actual base price from the organization (channel-aware for ePublisher).
      // Pass the resolved org directly so pricing fields like ePublisherCost are available.
      let basePrice = getOrganizationPrice(organizationId, channel, selectedSchool);
      
      if (!basePrice) {
        const fallbackPrice = (selectedSchool.isSchool === true || selectedSchool.isSchool === 1) ? 900 : 100;
        basePrice = fallbackPrice;
      }

      // Get the authentication token
      const token = await getToken();

      // Use the actual organization price instead of hardcoded 100
      const result = await PricingService.calculatePrice(
        basePrice,
        channel,
        adType,
        startDate,
        endDate,
        1, // quantity
        organizationId,
        token,
        addons
      );
      
      if (result && result.success && result.price) {
        setGroups(currentGroups => currentGroups.map(g => {
          if (g.id === groupId) {
            return {
              ...g,
              schools: g.schools.map(school => {
                if (school.id === schoolId) {
                  return {
                    ...school,
            price: result.price.toString()
          };
                }
                return school;
              })
            };
          }
          return g;
        }));
      }
    } catch (error) {
      console.error('Error calculating price:', error);
    }
  };

  // Check top spot availability for a school
  const checkTopSpotAvailability = async (groupId, schoolId, organizationId, channel, startDate = null, endDate = null) => {
    try {
      const result = await PricingService.checkTopSpotAvailability(organizationId, channel, startDate, endDate);
      
      const conflictKey = `${groupId}-${schoolId}`;
      
      // Update the conflicts state
      setTopSpotConflicts(prev => {
        const newConflicts = {
          ...prev,
          [conflictKey]: result.data.hasConflict || false
        };
        // Notify parent component of conflicts change
        if (onTopSpotConflictsChange) {
          onTopSpotConflictsChange(newConflicts);
        }
        return newConflicts;
      });
      
      return !result.data.hasConflict;
    } catch (error) {
      console.error('Error checking top spot availability:', error);
      // Clear any existing conflict for this school on error
      const conflictKey = `${groupId}-${schoolId}`;
      setTopSpotConflicts(prev => {
        const newConflicts = { ...prev };
        delete newConflicts[conflictKey];
        // Notify parent component of conflicts change
        if (onTopSpotConflictsChange) {
          onTopSpotConflictsChange(newConflicts);
        }
        return newConflicts;
      });
      return true; // Assume available on error to not block the user
    }
  };

  // Handle addon changes for a specific school
  const handleSchoolAddonChange = async (groupId, schoolId, addonType, checked) => {
    const group = groups.find(g => g.id === groupId);
    if (!group) return;
    
    const school = group.schools.find(s => s.id === schoolId);
    if (!school) return;
    
    // If selecting top spot, check availability
    if (addonType === 'topSpot' && checked) {
      if (school.organization_id && group.channel && group.startDate && group.endDate) {
        await checkTopSpotAvailability(groupId, schoolId, school.organization_id, group.channel, group.startDate, group.endDate);
      }
    }
    
    setGroups(groups.map(g => {
      if (g.id === groupId) {
        return {
          ...g,
          schools: g.schools.map(s => {
            if (s.id === schoolId) {
              const updatedAddons = { ...s.addons, [addonType]: checked };
              
              // Recalculate price when addons change
              if (s.organization_id && g.channel && g.adType && g.startDate && g.endDate) {
                calculateSchoolPrice(groupId, schoolId, s.organization_id, g.channel, g.adType, g.startDate, g.endDate, updatedAddons);
              }
              
              return { ...s, addons: updatedAddons };
            }
            return s;
          })
        };
      }
      return g;
    }));
    
    // Clear top spot conflict when unchecking top spot
    if (addonType === 'topSpot' && !checked) {
      const conflictKey = `${groupId}-${schoolId}`;
      setTopSpotConflicts(prev => {
        const newConflicts = { ...prev };
        delete newConflicts[conflictKey];
        // Notify parent component of conflicts change
        if (onTopSpotConflictsChange) {
          onTopSpotConflictsChange(newConflicts);
        }
        return newConflicts;
      });
    }
  };

  // Handle addon changes for a group (gif)
  const handleGroupAddonChange = async (groupId, addonType, checked) => {
    setGroups(groups.map(g => {
      if (g.id === groupId) {
        const updatedAddons = { ...g.addons, [addonType]: checked };
        
        // Recalculate prices for all schools when group addons change
        if (g.channel && g.adType && g.startDate && g.endDate) {
          g.schools.forEach(school => {
            if (school.organization_id) {
              // Merge group addons with school addons for price calculation
              const mergedAddons = { ...updatedAddons, ...(school.addons || {}) };
              calculateSchoolPrice(groupId, school.id, school.organization_id, g.channel, g.adType, g.startDate, g.endDate, mergedAddons);
            }
          });
        }
        
        return { ...g, addons: updatedAddons };
      }
      return g;
    }));
  };

  // Calculate subtotal for a school with $ or % discount support
  // If school discount is 0, apply group discount
  const calculateSchoolSubTotal = (school, group) => {
    const price = parseFloat(school.price || 0);
    const schoolDiscount = parseFloat(school.discount || 0);
    const groupDiscount = parseFloat(group.groupDiscount || 0);
    
    // Use school discount if set, otherwise use group discount
    const effectiveDiscount = schoolDiscount > 0 ? schoolDiscount : groupDiscount;
    const effectiveDiscountType = schoolDiscount > 0 ? (school.discountType || '$') : (group.groupDiscountType || '$');
    
    if (effectiveDiscount === 0) {
      return price;
    }
    
    let subtotal;
    if (effectiveDiscountType === '%') {
      // Percentage discount: subtract percentage of price
      const discountAmount = (price * effectiveDiscount) / 100;
      subtotal = Math.max(0, price - discountAmount);
    } else {
      // Dollar discount: subtract fixed amount
      subtotal = Math.max(0, price - effectiveDiscount);
    }
    
    return subtotal;
  };

  // Calculate subtotal for a group
  const calculateGroupSubTotal = (group) => {
    return group.schools.reduce((total, school) => {
      return total + calculateSchoolSubTotal(school, group);
    }, 0);
  };

  // Calculate total for all groups
  const calculateTotal = () => {
    return groups.reduce((total, group) => {
      return total + calculateGroupSubTotal(group);
    }, 0);
  };

  return (
    <Box sx={{ pt: 2 }}>
      <Grid container spacing={3}>
        {/* Business Type Selection */}
        <Grid item xs={12}>
          <FormControl component="fieldset">
            <FormLabel component="legend">
              <Typography variant="subtitle1" gutterBottom>
                Business Type
              </Typography>
            </FormLabel>
            <RadioGroup
              row
              value={formData.businessType || 'existing'}
              onChange={handleBusinessTypeChange}
            >
              <FormControlLabel value="existing" control={<Radio />} label="Existing Business" />
              <FormControlLabel value="new" control={<Radio />} label="New Business" />
            </RadioGroup>
          </FormControl>
        </Grid>

        {/* Business Selection/Input */}
        <Grid item xs={12}>
          {lockedOrganization ? (
            // Display locked organization (read-only)
            <Box>
              <TextField
                fullWidth
                label="Business / Organisation"
                value={lockedOrganization.name}
                disabled
                InputProps={{
                  readOnly: true,
                }}
                helperText="Organisation is locked for this offer (from original order)"
              />
            </Box>
          ) : formData.businessType === 'new' ? (
            <TextField
              fullWidth
              label="Business Name"
              value={formData.business_name || ''}
              onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
              required
            />
          ) : (
            <EnhancedSearch
              label="Select Business"
              placeholder="Search for existing business..."
              endpoint="/organizations/search?type=business"
              value={selectedBusinessOrg}
              onSelect={(selectedOrg) => {
                // Cache full object so value doesn't depend on limited local array.
                setSelectedBusinessOrg(selectedOrg || null);
                setFormData({
                  ...formData,
                  organization_id: selectedOrg?.id || '',
                  business_name: selectedOrg?.name || ''
                });
              }}
              limitResults={15}
              filterOptions={(options) => options.filter(opt => 
                opt.isBusiness === true || opt.isBusiness === 1
              )}
              required
            />
          )}
        </Grid>

        {/* Groups Section */}
          <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
            Offer Details
              </Typography>
        </Grid>

        {groups.map((group, groupIndex) => (
          <Grid item xs={12} key={group.id}>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" fontWeight={600}>
                    Group {groupIndex + 1}
              </Typography>
                  {groups.length > 1 && (
                    <IconButton 
                      size="small" 
                      color="error" 
                      onClick={() => removeGroup(group.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>

              <Grid container spacing={2}>
                  {/* First Line: Channel, Ad Type, Start Date, End Date */}
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth required>
                    <InputLabel>Channel</InputLabel>
                    <Select
                        value={group.channel || ''}
                        onChange={(e) => handleGroupChange(group.id, 'channel', e.target.value)}
                      label="Channel"
                    >
                        <MenuItem value="SchoolLink">SchoolLink</MenuItem>
                      <MenuItem value="ePublisher">ePublisher</MenuItem>
                      <MenuItem value="Paper">Paper</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth required>
                    <InputLabel>Ad Type</InputLabel>
                    <Select
                        value={group.adType || ''}
                        onChange={(e) => handleGroupChange(group.id, 'adType', e.target.value)}
                      label="Ad Type"
                        disabled={!group.channel}
                      >
                        {group.channel ? (
                          getAvailableAdTypes(group.channel).map(adType => (
                        <MenuItem key={adType} value={adType}>{adType}</MenuItem>
                          ))
                        ) : (
                          <MenuItem value="" disabled>Select a channel first</MenuItem>
                        )}
                    </Select>
                  </FormControl>
                </Grid>
                
                  <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                      value={group.startDate || ''}
                      onChange={(e) => {
                        handleGroupChange(group.id, 'startDate', e.target.value);
                        // If POW is selected, automatically update end date to 5 weeks after new start date
                        if (group.adType === 'POW' && e.target.value) {
                          const startDate = new Date(e.target.value);
                          const endDate = new Date(startDate);
                          endDate.setDate(endDate.getDate() + (5 * 7)); // 5 weeks = 35 days
                          setTimeout(() => {
                            handleGroupChange(group.id, 'endDate', endDate.toISOString().split('T')[0]);
                          }, 0);
                        }
                      }}
                    InputLabelProps={{ shrink: true }}
                      required
                  />
                </Grid>
                
                  <Grid item xs={12} sm={6} md={3}>
                    {group.adType === 'POW' ? (
                      <TextField
                        fullWidth
                        label="End Date (POW Fixed)"
                        type="date"
                        value={group.endDate || ''}
                        disabled
                        helperText="POW ads have a fixed 5-week duration"
                        InputLabelProps={{ shrink: true }}
                      />
                    ) : (
                      <DurationSelector
                        startDate={group.startDate}
                        endDate={group.endDate}
                        onEndDateChange={(value) => handleGroupChange(group.id, 'endDate', value)}
                        label="End Date"
                        defaultDuration={{ value: 1, unit: 'years' }}
                      />
                    )}
                </Grid>
                
                  {/* Second Line: Group Discount, GIF Addon, Group Total */}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                      label="Group Discount (applies to all schools)"
                    type="number"
                      value={group.groupDiscount || ''}
                      onChange={(e) => handleGroupChange(group.id, 'groupDiscount', e.target.value)}
                      placeholder="0"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                            <Select
                              value={group.groupDiscountType || '$'}
                              onChange={(e) => handleGroupChange(group.id, 'groupDiscountType', e.target.value)}
                              variant="standard"
                              disableUnderline
                            >
                              <MenuItem value="$">$</MenuItem>
                              <MenuItem value="%">%</MenuItem>
                            </Select>
                        </InputAdornment>
                        ),
                    }}
                  />
                </Grid>

                  {/* GIF Addon Option */}
                  {((group.channel === 'ePublisher' && group.adType === 'Premium') ||
                    (group.adType === 'POW' && ['ePublisher', 'Paper'].includes(group.channel))) && (
                    <Grid item xs={12} sm={6} md={4}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={group.addons?.gif || false}
                            onChange={(e) => handleGroupAddonChange(group.id, 'gif', e.target.checked)}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" fontWeight="600">
                              Gif {group.adType === 'POW' ? '(+$100)' : '(+$10/week)'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {group.adType === 'POW' 
                                ? 'Special POW pricing' 
                                : 'Animated content option'
                              }
                            </Typography>
                          </Box>
                        }
                        sx={{ mt: 1 }}
                      />
          </Grid>
        )}

                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', pt: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        Group Total: 
          </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, ml: 1, color: 'primary.main' }}>
                        ${calculateGroupSubTotal(group).toFixed(2)}
                      </Typography>
                    </Box>
        </Grid>

                  {/* Schools in this group */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mr: 1 }}>
                        Schools
                </Typography>
                      <Chip label={group.schools.length} size="small" color="primary" />
                    </Box>
                    
                    {group.schools.map((school, schoolIndex) => (
                      <Paper key={school.id} sx={{ p: 1.5, mb: 0.5, bgcolor: 'grey.50' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                          {group.schools.length > 1 && (
                  <IconButton 
                    size="small" 
                    color="error" 
                              onClick={() => removeSchoolFromGroup(group.id, school.id)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>

              <Grid container spacing={2}>
                        {/* Single Line: School, Top Spot, Price, Discount, Subtotal */}
                        <Grid item xs={12} md={4}>
                  <EnhancedSearch
                    label="Select School"
                    placeholder="Search for school..."
                    endpoint="/organizations/search?type=school"
                            value={school._selectedOrg || null}
                    onSelect={(selectedSchool) => {
                              // Pass the full org object so it gets cached in school state —
                              // avoids depending on the limited local array for value.
                              handleSchoolChange(group.id, school.id, 'organization_id', selectedSchool?.id || '', selectedSchool || null);
                    }}
                    limitResults={15}
                            filterOptions={(options) => {
                              // Get all selected school IDs in this group (excluding current school)
                              const selectedSchoolIds = group.schools
                                .filter(s => s.id !== school.id && s.organization_id)
                                .map(s => parseInt(s.organization_id));
                              
                              return options.filter(opt => {
                                // Exclude already selected schools in this group
                                if (selectedSchoolIds.includes(opt.id)) return false;
                                
                                // Basic filters
                                const isActiveSchool = (opt.isSchool === true || opt.isSchool === 1) &&
                      (opt.schoolStatus === 'Active' || !opt.schoolStatus) &&
                                  (opt.paymentModel === 'Ad Supported' || opt.paymentModel !== 'Paid');
                                
                                if (!isActiveSchool) return false;
                                
                                // Filter by channel if group has a channel selected
                                if (group.channel) {
                                  if (group.channel === 'SchoolLink') {
                                    return opt.hasSchoolLink === true || opt.hasSchoolLink === 1;
                                  } else if (group.channel === 'ePublisher') {
                                    return opt.hasEPublisher === true || opt.hasEPublisher === 1;
                                  } else if (group.channel === 'Paper') {
                                    return opt.hasPaper === true || opt.hasPaper === 1;
                                  }
                                }
                                
                                return true;
                              });
                            }}
                    required
                  />
                </Grid>

                        {/* Top Spot Option - Compact */}
                        {group.channel === 'ePublisher' && 
                         ['Standard', 'Premium'].includes(group.adType) &&
                         group.adType !== 'POW' && school.organization_id && (
                          <Grid item xs={12} md={1}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              <Typography variant="caption" sx={{ mb: 0.5 }}>
                                Top Spot
                      </Typography>
                              <Tooltip title="Top Spot (+$5/week) - Minimum 12 weeks">
                            <Checkbox
                                  checked={school.addons?.topSpot || false}
                                  onChange={(e) => handleSchoolAddonChange(group.id, school.id, 'topSpot', e.target.checked)}
                                  color={hasTopSpotDurationIssue(school, group) || topSpotConflicts[`${group.id}-${school.id}`] ? 'warning' : 'primary'}
                                />
                              </Tooltip>
                    </Box>
                  </Grid>
                )}

                        <Grid item xs={12} md={2}>
                          <TextField
                            fullWidth
                            label="Price"
                            value={school.manualPriceOverride ? (school.price || '') : (school.price ? `$${parseFloat(school.price).toFixed(2)}` : '$0.00')}
                            onChange={(e) => {
                              if (school.manualPriceOverride) {
                                // Remove $ and any non-numeric characters except decimal point
                                const numericValue = e.target.value.replace(/[^0-9.]/g, '');
                                handleSchoolChange(group.id, school.id, 'price', numericValue);
                              }
                            }}
                            InputProps={{
                              readOnly: !school.manualPriceOverride,
                              startAdornment: school.manualPriceOverride ? <InputAdornment position="start">$</InputAdornment> : null,
                            }}
                            size="small"
                            type={school.manualPriceOverride ? "number" : "text"}
                          />
                </Grid>

                        <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Discount"
                    type="number"
                            value={(school.discount && school.discount !== '0') ? school.discount : (group.groupDiscount || '')}
                            onChange={(e) => handleSchoolChange(group.id, school.id, 'discount', e.target.value)}
                            placeholder={(group.groupDiscount && group.groupDiscount !== '0') ? group.groupDiscount : '0'}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Select
                                    value={(school.discount && school.discount !== '0') ? (school.discountType || '$') : (group.groupDiscountType || '$')}
                                    onChange={(e) => handleSchoolChange(group.id, school.id, 'discountType', e.target.value)}
                            variant="standard"
                            disableUnderline
                          >
                            <MenuItem value="$">$</MenuItem>
                            <MenuItem value="%">%</MenuItem>
                          </Select>
                        </InputAdornment>
                      ),
                    }}
                            size="small"
                  />
                </Grid>

                        <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Subtotal"
                            value={`$${calculateSchoolSubTotal(school, group).toFixed(2)}`}
                    InputProps={{ readOnly: true }}
                            size="small"
                  />
                </Grid>

                        <Grid item xs={12} md={1}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                            <Typography variant="caption" sx={{ mb: 0.5, textAlign: 'center', color: 'text.secondary' }}>
                              Price
                            </Typography>
                            <Tooltip title={school.manualPriceOverride ? 'Manual price — click to change' : 'Auto-calculated — click to change'}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  setPriceMenuAnchor(e.currentTarget);
                                  setPriceMenuTarget({ groupId: group.id, schoolId: school.id });
                                }}
                                color={school.manualPriceOverride ? 'warning' : 'primary'}
                              >
                                {school.manualPriceOverride ? <EditIcon fontSize="small" /> : <AutorenewIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Grid>

                        {/* Advertising Objections */}
                        {school._selectedOrg?.advertisingObjectives && (
                          <Grid item xs={12}>
                            <Alert severity="warning" sx={{ mt: 1 }}>
                              <Typography variant="body2">
                                <strong>Advertising Objections:</strong> {school._selectedOrg.advertisingObjectives}
                              </Typography>
                            </Alert>
                          </Grid>
                        )}

                        {/* Warnings below the row */}
                        {school.organization_id && (
                          <Grid item xs={12}>
                            <Collapse in={hasTopSpotDurationIssue(school, group)}>
                              <Alert severity="warning" sx={{ mt: 1 }}>
                                <Typography variant="body2">
                                  <strong>Top Spot:</strong> Requires minimum 12 weeks. Current: {calculateWeeks(group.startDate, group.endDate)} weeks.
                                </Typography>
                              </Alert>
                            </Collapse>
                            
                            <Collapse in={topSpotConflicts[`${group.id}-${school.id}`] === true}>
                              <Alert severity="warning" sx={{ mt: 1 }}>
                                <Typography variant="body2">
                                  <strong>Top Spot Conflict:</strong> This school already has Top Spot for {group.channel} during these dates.
                                </Typography>
                              </Alert>
                            </Collapse>
                          </Grid>
                        )}
              </Grid>
            </Paper>
                    ))}

                    {/* Add Another School Button */}
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => addSchoolToGroup(group.id)}
                      sx={{ mt: 1 }}
                    >
                      Add Another School
                    </Button>
                  </Grid>

                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {/* Add Group Button */}
        <Grid item xs={12}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addGroup}
          >
            Add Another Group
          </Button>
        </Grid>

        {/* Client Change Requests Section - Only show when editing */}
        {isEditingOffer && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2, bgcolor: 'warning.lighter', border: '1px solid', borderColor: 'warning.light' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <WarningIcon sx={{ color: 'warning.main', mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'warning.dark' }}>
                  Client Requested Changes
                </Typography>
              </Box>
              
              {loadingChangeRequests ? (
                <Typography variant="body2" color="text.secondary">
                  Loading change requests...
                </Typography>
              ) : changeRequests.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No change requests for this offer.
                </Typography>
              ) : (
                <Stack spacing={2}>
                  {changeRequests.map((request, index) => (
                    <Paper key={request.id} sx={{ p: 2, bgcolor: 'white' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Request #{index + 1} - {formatDate(request.requestedAt)}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
                        {request.changeNotes}
                      </Typography>
                      
                      {/* Attachment Display */}
                      {request.attachmentOriginalName && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                            Attachment:
                          </Typography>
                          {isImageFile(request.attachmentOriginalName) ? (
                            <Card sx={{ maxWidth: 300, mb: 1 }}>
                              <CardMedia
                                component="img"
                                height="120"
                                image={getAttachmentUrl(request.attachmentPath)}
                                alt={request.attachmentOriginalName}
                                sx={{ 
                                  cursor: 'pointer',
                                  objectFit: 'cover'
                                }}
                                onClick={() => handleViewImage(request.attachmentPath, request.attachmentOriginalName)}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                              <Box 
                                sx={{ 
                                  display: 'none',
                                  height: 120,
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  bgcolor: 'grey.100',
                                  color: 'text.secondary'
                                }}
                              >
                                <ImageIcon />
                                <Typography variant="caption" sx={{ ml: 1 }}>
                                  Image not available
                                </Typography>
                              </Box>
                              <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <Typography variant="caption" sx={{ flex: 1, mr: 1 }}>
                                    {request.attachmentOriginalName}
                                  </Typography>
                                  <Box>
                                    <Tooltip title="View full size">
                                      <IconButton 
                                        size="small" 
                                        onClick={() => handleViewImage(request.attachmentPath, request.attachmentOriginalName)}
                                      >
                                        <VisibilityIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Download">
                                      <IconButton 
                                        size="small" 
                                        onClick={() => handleDownloadAttachment(request.attachmentPath, request.attachmentOriginalName)}
                                      >
                                        <DownloadIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </Box>
                              </CardContent>
                            </Card>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip 
                                label={`📎 ${request.attachmentOriginalName}`}
                                size="small"
                                variant="outlined"
                              />
                              <Tooltip title="Download">
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleDownloadAttachment(request.attachmentPath, request.attachmentOriginalName)}
                                >
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </Box>
                      )}
                      
                      {/* Processed Status */}
                      {request.processed === true && (
                        <Box sx={{ mt: 1, p: 1, bgcolor: 'success.light', borderRadius: 1 }}>
                          <Typography variant="caption" color="success.dark">
                            ✓ Processed by {request.processedByName} on {formatDate(request.processedAt)}
                          </Typography>
                          {request.responseNotes && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              Response: {request.responseNotes}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Paper>
                  ))}
                </Stack>
              )}
            </Paper>
          </Grid>
        )}

        {/* Offer Image Section */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ImageIcon fontSize="small" />
              Offer Image
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              Optionally attach an image to this offer (e.g. a sample ad, rate card, or visual). The caption will appear above the image in the generated offer document.
            </Typography>

            {/* Caption text box */}
            <TextField
              label="Image Caption / Explanation"
              multiline
              rows={3}
              fullWidth
              variant="outlined"
              value={formData.imageCaption || ''}
              onChange={(e) => setFormData({ ...formData, imageCaption: e.target.value })}
              placeholder="Describe what the image shows or provide any context the client needs…"
              sx={{ mb: 2 }}
            />

            {/* Image picker */}
            {normalizeImageDataUrl(formData.imageData) ? (
              <Box>
                <Box
                  component="img"
                  src={normalizeImageDataUrl(formData.imageData)}
                  alt="Offer image preview"
                  sx={{ maxWidth: '100%', maxHeight: 300, borderRadius: 1, border: '1px solid', borderColor: 'divider', display: 'block', mb: 1 }}
                />
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={() => setFormData({ ...formData, imageData: '', imageCaption: formData.imageCaption })}
                >
                  Remove Image
                </Button>
              </Box>
            ) : (
              <Box>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<ImageIcon />}
                >
                  Upload Image
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      e.target.value = '';
                      // Compress/resize like other attachments - offer images are
                      // often full-size phone photos (several MB) which are slow
                      // to store/transfer as base64 and can fail to load as a
                      // preview once round-tripped through the database.
                      let processedFile = file;
                      try {
                        processedFile = await compressImageIfNeeded(file, getCompressionOptions('offer'));
                      } catch (compressionError) {
                        console.error('Error compressing offer image, using original:', compressionError);
                      }
                      const reader = new FileReader();
                      reader.onload = (ev) => {
                        setFormData({ ...formData, imageData: ev.target.result });
                      };
                      reader.readAsDataURL(processedFile);
                    }}
                  />
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  JPG, PNG, GIF — max 5 MB recommended
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Offer Attachments */}
        <Grid item xs={12}>
          {isEditingOffer && editingOfferId ? (
            <OrderAttachments
              orderId={editingOfferId}
              mode="full"
              title="Offer Attachments"
              attachments={savedAttachments}
              onAttachmentsChange={(updatedAttachments) => setSavedAttachments(updatedAttachments)}
              tempAttachments={tempAttachments}
              onTempAttachmentsChange={handleAttachmentsChange}
              type="offer"
            />
          ) : (
            <OrderCreationAttachments
              attachments={tempAttachments}
              onAttachmentsChange={handleAttachmentsChange}
              title="Offer Attachments"
              maxFiles={10}
            />
          )}
        </Grid>

        {/* Notes Section - Embedded */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, mt: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AddIcon fontSize="small" />
              Add New Note
            </Typography>
            
            {/* Note Type Selector */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" gutterBottom display="block">
                Note Type:
              </Typography>
              <Stack direction="row" spacing={1}>
                <Chip
                  label="General"
                  variant={noteType === 'general' ? 'filled' : 'outlined'}
                  color={noteType === 'general' ? 'primary' : 'default'}
                  onClick={() => setNoteType('general')}
                  size="small"
                />
                <Chip
                  label="Design"
                  variant={noteType === 'design' ? 'filled' : 'outlined'}
                  color={noteType === 'design' ? 'secondary' : 'default'}
                  onClick={() => setNoteType('design')}
                  size="small"
                />
              </Stack>
            </Box>
            
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="Enter your note here..."
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              variant="outlined"
              sx={{ mb: 1 }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddNote}
                disabled={!newNote.trim()}
              >
                Add Note
              </Button>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Notes History - Categorized */}
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <NotesIcon fontSize="small" />
              Notes History ({getTotalNoteCount()})
            </Typography>
            
            {getTotalNoteCount() === 0 ? (
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
                <Typography variant="body2" color="text.secondary">
                  No notes available. Add the first note above.
                </Typography>
              </Paper>
            ) : (
              <Stack spacing={3}>
                {/* Sales Notes Section */}
                {(() => {
                  const salesNotes = offerNotes.filter(note => !note.noteType || note.noteType === 'general');
                  return salesNotes.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" color="primary" fontWeight="bold" gutterBottom>
                        📊 Sales Notes ({salesNotes.length})
                      </Typography>
                      <Stack spacing={1}>
                        {salesNotes.map((note, index) => (
                          <NoteCard key={note.id || `sales-${index}`} note={note} color="primary" bgColor="blue.50" borderColor="blue.200" />
                        ))}
                      </Stack>
                    </Box>
                  );
                })()}

                {/* Design Notes Section */}
                {(() => {
                  const designNotes = offerNotes.filter(note => note.noteType === 'design');
                  return designNotes.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" color="secondary" fontWeight="bold" gutterBottom>
                        🎨 Design Notes ({designNotes.length})
                      </Typography>
                      <Stack spacing={1}>
                        {designNotes.map((note, index) => (
                          <NoteCard key={note.id || `design-${index}`} note={note} color="secondary" bgColor="purple.50" borderColor="purple.200" />
                        ))}
                      </Stack>
                    </Box>
                  );
                })()}

                {/* Customer Notes Section */}
                {(() => {
                  const customerNotes = offerNotes.filter(note => note.noteType === 'customer');
                  return customerNotes.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" color="info.main" fontWeight="bold" gutterBottom>
                        👤 Customer Notes ({customerNotes.length})
                      </Typography>
                      <Stack spacing={1}>
                        {customerNotes.map((note, index) => (
                          <NoteCard key={note.id || `customer-${index}`} note={note} color="info" bgColor="info.50" borderColor="info.200" />
                        ))}
                      </Stack>
                    </Box>
                  );
                })()}
              </Stack>
            )}
          </Paper>
        </Grid>

        {/* Total */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography variant="h6" textAlign="right">
              Total: ${(calculateTotal() || 0).toFixed(2)}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Image Viewer Dialog */}
      {isEditingOffer && (
        <Dialog 
          open={imageViewerDialog.open} 
          onClose={() => setImageViewerDialog({ open: false, imageUrl: '', imageName: '' })} 
          maxWidth="md" 
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {imageViewerDialog.imageName}
            <IconButton 
              onClick={() => setImageViewerDialog({ open: false, imageUrl: '', imageName: '' })}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                minHeight: 400,
                bgcolor: 'grey.100'
              }}
            >
              <Box
                component="img"
                src={imageViewerDialog.imageUrl}
                alt={imageViewerDialog.imageName}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <Box 
                sx={{ 
                  display: 'none',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'text.secondary',
                  minHeight: 200
                }}
              >
                <ImageIcon sx={{ fontSize: 48, mb: 1 }} />
                <Typography variant="body1">Unable to load image</Typography>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button 
              startIcon={<DownloadIcon />} 
              onClick={() => {
                const urlParts = imageViewerDialog.imageUrl.split('/uploads/');
                if (urlParts.length > 1) {
                  handleDownloadAttachment(urlParts[1], imageViewerDialog.imageName);
                }
              }}
            >
              Download
            </Button>
            <Button onClick={() => setImageViewerDialog({ open: false, imageUrl: '', imageName: '' })}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Change Price menu — shared across all schools, anchors to whichever icon was clicked */}
      <Menu
        anchorEl={priceMenuAnchor}
        open={Boolean(priceMenuAnchor)}
        onClose={() => { setPriceMenuAnchor(null); setPriceMenuTarget(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MenuItem
          onClick={() => {
            if (priceMenuTarget) {
              const { groupId, schoolId } = priceMenuTarget;
              // Setting manualPriceOverride → false triggers recalculation via
              // the existing handleSchoolChange path (setTimeout + calculateSchoolPrice).
              handleSchoolChange(groupId, schoolId, 'manualPriceOverride', false);
            }
            setPriceMenuAnchor(null);
            setPriceMenuTarget(null);
          }}
        >
          <ListItemIcon><AutorenewIcon fontSize="small" color="primary" /></ListItemIcon>
          <ListItemText primary="Auto-calculate" secondary="Recalculate from current pricing" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (priceMenuTarget) {
              const { groupId, schoolId } = priceMenuTarget;
              handleSchoolChange(groupId, schoolId, 'manualPriceOverride', true);
            }
            setPriceMenuAnchor(null);
            setPriceMenuTarget(null);
          }}
        >
          <ListItemIcon><EditIcon fontSize="small" color="warning" /></ListItemIcon>
          <ListItemText primary="Enter manually" secondary="Type a custom price" />
        </MenuItem>
      </Menu>
    </Box>
  );
};

// Export helper function for external validation
export const checkOfferTopSpotDurationRequirements = (audiences = []) => {
  // Check each audience for Top Spot duration issues
  return !audiences.some(audience => {
    // Create a school object and group object for validation
    const school = {
      addons: audience.addons || {}
    };
    const group = {
      startDate: audience.startDate,
      endDate: audience.endDate
    };
    return hasTopSpotDurationIssue(school, group);
  });
};

// Helper function to check if there are any top spot conflicts
export const checkOfferTopSpotAvailabilityRequirements = (topSpotConflicts = {}) => {
  return !Object.values(topSpotConflicts).some(hasConflict => hasConflict === true);
};

// Combined validation for both duration and availability
export const checkOfferTopSpotRequirements = (audiences = [], topSpotConflicts = {}) => {
  return checkOfferTopSpotDurationRequirements(audiences) && 
         checkOfferTopSpotAvailabilityRequirements(topSpotConflicts);
};

export default OfferForm; 
